import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { weeks, monthlyGuidance } from '../../data/fifty2Tuesdays';
import { weekDate, fmtDate, startsMonth } from './useFifty2Progress';
import { weekTitle } from './WeekGrid';
import { Practice, Gifts, Label } from './WeekSections';
import BreathingExercise from './BreathingExercise';

const Journal = ({ n, entry, status, onReflect, onComplete, signedIn }) => {
  if (!signedIn) return <p className="font-body text-stone-600 italic" data-testid="f2-login-note">Log in from the Programs page to save your reflections and track your weeks.</p>;
  const done = entry?.completed_at;
  return (
    <section data-testid="f2-journal">
      <Label>My reflections</Label>
      <textarea
        value={entry?.reflection || ''}
        onChange={(e) => onReflect(n, e.target.value)}
        placeholder="Write your reflections here..."
        rows={6}
        className="w-full rounded-2xl border border-[#e4d8c3] bg-white p-4 font-body text-stone-700 focus:outline-none focus:border-[#2b3550]"
        data-testid="f2-reflection"
      />
      <div className="flex flex-wrap items-center gap-4 mt-3">
        <button
          onClick={() => onComplete(n, !done)}
          className={`inline-flex items-center gap-2 font-body text-sm px-5 py-2.5 rounded-full transition-colors ${done ? 'bg-emerald-600 text-white' : 'bg-[#2b3550] text-[#f4ead8] hover:bg-[#1f2740]'}`}
          data-testid="f2-complete-btn"
        >
          <Check size={15} /> {done ? 'Completed' : 'Mark complete'}
        </button>
        {done && <span className="font-body text-sm text-stone-500" data-testid="f2-completed-date">Completed {fmtDate(new Date(done))} · tap again to undo</span>}
        <span className="font-body text-xs text-stone-500 ml-auto" data-testid="f2-save-status">
          {status === 'saving' ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Saving…</span> : status === 'saved' ? 'Saved' : status === 'error' ? 'Couldn’t save — check your connection' : ''}
        </span>
      </div>
    </section>
  );
};

export default function WeekView({ n, progress, canOpen, onNav, journal }) {
  const [breathing, setBreathing] = useState(false);
  const w = weeks[n - 1];
  const show = progress.show_dates;
  const { title, theme } = weekTitle(w, show);
  const month = show && startsMonth(progress.start_date, n) ? monthlyGuidance[weekDate(progress.start_date, n).getMonth() + 1] : null;
  const nav = (d) => canOpen(n + d) && onNav(n + d);
  return (
    <article className="max-w-3xl mx-auto space-y-8" data-testid={`f2-week-${n}`}>
      <div className="relative rounded-3xl overflow-hidden aspect-[16/9] bg-stone-200">
        <img src={w.image} alt="" className="w-full h-full object-cover" style={{ objectPosition: w.imagePos }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1f2740]/85 via-[#1f2740]/20 to-transparent" />
        <div className="absolute bottom-0 p-6 md:p-8 text-[#f4ead8]">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-[#e9b872]">Week {n}{show && ` · ${fmtDate(weekDate(progress.start_date, n))}`}</p>
          <h2 className="font-heading text-3xl md:text-5xl mt-1 text-[#f4ead8]" data-testid="f2-week-title">{title}</h2>
          <p className="font-body text-[#f4ead8]/85 mt-1">{theme}</p>
        </div>
      </div>
      {month && (
        <section className="border-l-4 border-[#b07d3c] bg-white rounded-r-2xl p-5" data-testid="f2-monthly-guidance">
          <Label>Monthly guidance · {month.title}</Label>
          <p className="font-body text-stone-700 leading-relaxed">{month.guidance}</p>
        </section>
      )}
      {show && (
        <section data-testid="f2-cosmic">
          <Label>Cosmic context</Label>
          <p className="font-body text-stone-700 leading-relaxed">{w.cosmic}</p>
        </section>
      )}
      <Practice week={w} onBreathe={() => setBreathing(true)} />
      <Gifts week={w} />
      <Journal n={n} entry={progress.weeks[n]} {...journal} status={journal.status.week === n ? journal.status.state : 'idle'} />
      <div className="flex justify-between pt-4 border-t border-[#e4d8c3]">
        <button onClick={() => nav(-1)} disabled={n === 1 || !canOpen(n - 1)} className="inline-flex items-center gap-1 font-body text-sm text-[#2b3550] disabled:opacity-30" data-testid="f2-prev-week"><ChevronLeft size={16} /> {n > 1 ? `Week ${n - 1}` : 'Previous'}</button>
        <button onClick={() => nav(1)} disabled={n === 52 || !canOpen(n + 1)} className="inline-flex items-center gap-1 font-body text-sm text-[#2b3550] disabled:opacity-30" data-testid="f2-next-week">{n < 52 ? `Week ${n + 1}` : 'Next'} <ChevronRight size={16} /></button>
      </div>
      {breathing && <BreathingExercise onClose={() => setBreathing(false)} />}
    </article>
  );
}
